import { useEffect, useState } from "react";
import { Plus, X, Shield, Key, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";
import ConfirmDialog from "./ConfirmDialog";

const MODULES = [
  { key: "customers", label: "Customers" },
  { key: "estimates", label: "Estimates" },
  { key: "invoices", label: "Invoices" },
  { key: "jobs", label: "Jobs" },
  { key: "pricebook", label: "Price Book" },
  { key: "settings", label: "Settings" },
  { key: "reports", label: "Reports" },
  { key: "expenses", label: "Expenses" },
  { key: "timesheets", label: "Timesheets" },
  { key: "employees", label: "Employees" },
];

const ROLE_DESCRIPTIONS = {
  admin: "Full access to every module, including settings and team management.",
  office: "Books, billing, customers — everything except Settings and Employees.",
  field_tech: "Only own jobs, own timesheet, and Price Book view. No financials.",
  custom: "Pick each module's access individually below.",
};

export default function EmployeeAccountsManager() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dlg, setDlg] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (e) {
      // /users may not be exposed; fall back to employees
      try {
        const { data } = await api.get("/employees");
        setUsers((data || []).map((e) => ({ id: e.user_id || e.id, email: e.email, name: e.name, role: e.role, active: e.active })));
      } catch { setUsers([]); }
    }
  };
  useEffect(() => { load(); }, []);

  const newAccount = () => {
    setEditing({
      _new: true,
      email: "", name: "", password: "",
      role: "field_tech", permissions: null,
      active: true, hourly_rate: 0,
    });
    setShowForm(true);
  };

  const editAccount = (u) => {
    setEditing({
      _new: false, id: u.id,
      email: u.email, name: u.name,
      role: u.role || "field_tech",
      permissions: u.permissions || null,
      active: u.active !== false,
      new_password: "",
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (editing._new) {
        await api.post("/employees-account", {
          email: editing.email.trim(),
          name: editing.name,
          password: editing.password,
          role: editing.role,
          permissions: editing.role === "custom" ? (editing.permissions || {}) : null,
          active: editing.active,
          hourly_rate: Number(editing.hourly_rate || 0),
        });
        setMsg(`Created ${editing.email}. They can log in immediately.`);
      } else {
        await api.put(`/employees-account/${editing.id}`, {
          role: editing.role,
          permissions: editing.role === "custom" ? (editing.permissions || {}) : null,
          active: editing.active,
          new_password: editing.new_password || null,
        });
        setMsg(`${editing.email} updated.`);
      }
      setTimeout(() => setMsg(""), 4000);
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const updatePerm = (modKey, value) => {
    setEditing({
      ...editing,
      permissions: { ...(editing.permissions || {}), [modKey]: value },
    });
  };

  // Part 23 (Feb 2026) — remove employee's account + login.
  const requestRemove = (u) => {
    setDlg({
      title: `Remove ${u.name || u.email}?`,
      body: `${u.email} will lose access immediately. Their login and permissions will be permanently deleted. Historical records they created (jobs, timesheets, notes) are kept for audit.`,
      confirmLabel: "Remove access",
      danger: true,
      onConfirm: async () => {
        setErr("");
        try {
          await api.delete(`/employees-account/${u.id}`);
          setMsg(`${u.email} was removed.`);
          setTimeout(() => setMsg(""), 4000);
          load();
        } catch (e) { setErr(formatErr(e)); }
      },
    });
  };

  return (
    <div data-testid="employee-accounts-manager" className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div className="border-b border-slate-100 pb-3 mb-2 flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Shield size={16}/> Employee accounts & permissions</h3>
          <p className="text-xs text-slate-500 mt-1">
            Each employee gets their own email + password to log in. Pick a role or customize module-level access.
          </p>
        </div>
        <button onClick={newAccount} data-testid="new-employee-account-btn" className="px-3 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-xs rounded-md font-medium flex items-center gap-1.5"><Plus size={12}/>Add account</button>
      </div>

      {msg && <div data-testid="emp-account-msg" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded">{msg}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Name</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Email</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Role</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 text-sm">No employee accounts yet.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={`emp-account-row-${u.id}`} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{u.name}</td>
                <td className="px-3 py-2 text-slate-700">{u.email}</td>
                <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{(u.role || "—").replace("_", " ")}</span></td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${u.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{u.active !== false ? "Active" : "Inactive"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => editAccount(u)} data-testid={`emp-edit-${u.id}`} className="text-xs text-[#0F4C81] hover:underline">Edit</button>
                    <button
                      onClick={() => requestRemove(u)}
                      data-testid={`emp-remove-${u.id}`}
                      title="Remove access"
                      className="text-xs text-rose-700 hover:underline flex items-center gap-1"
                    >
                      <Trash2 size={12}/> Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="emp-account-form" className="bg-white rounded-md w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl font-bold flex items-center gap-2"><Shield size={18}/>{editing._new ? "New employee account" : `Edit ${editing.email}`}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

            {editing._new && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-700">Name *</label>
                  <input data-testid="emp-form-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-700">Email *</label>
                  <input data-testid="emp-form-email" type="email" required value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-700 flex items-center gap-1"><Key size={11}/> Password *</label>
                  <input data-testid="emp-form-password" type="text" required value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Minimum 6 characters" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded font-mono"/>
                  <p className="text-[10px] text-slate-500 mt-1">Share this with the employee — they can change it after first login.</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-700">Hourly rate ($)</label>
                  <input data-testid="emp-form-rate" type="number" step="0.01" value={editing.hourly_rate} onChange={(e) => setEditing({ ...editing, hourly_rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded tabular"/>
                </div>
              </div>
            )}

            {!editing._new && (
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-700 flex items-center gap-1"><Key size={11}/> Reset password (optional)</label>
                <input data-testid="emp-form-new-password" type="text" value={editing.new_password || ""} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })} placeholder="Leave blank to keep current password" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded font-mono"/>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-700">Role</label>
              <select data-testid="emp-form-role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded">
                <option value="admin">Admin — full access</option>
                <option value="office">Office — books & customers, no settings</option>
                <option value="field_tech">Field Tech — own jobs only</option>
                <option value="custom">Custom — pick modules below</option>
              </select>
              <p className="text-[11px] text-slate-500 mt-1">{ROLE_DESCRIPTIONS[editing.role]}</p>
            </div>

            {editing.role === "custom" && (
              <div data-testid="emp-form-custom-perms" className="border border-slate-200 rounded p-3 bg-slate-50/40">
                <div className="text-xs uppercase tracking-wider text-slate-700 mb-2">Module access</div>
                <div className="grid grid-cols-2 gap-2">
                  {MODULES.map((m) => {
                    const v = (editing.permissions || {})[m.key];
                    return (
                      <label key={m.key} className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded p-2">
                        <span className="text-slate-700">{m.label}</span>
                        <select
                          data-testid={`perm-${m.key}`}
                          value={v === true ? "full" : v === "own" ? "own" : v === "view" ? "view" : "none"}
                          onChange={(e) => {
                            const map = { full: true, own: "own", view: "view", none: false };
                            updatePerm(m.key, map[e.target.value]);
                          }}
                          className="text-xs px-2 py-1 border border-slate-300 rounded bg-white"
                        >
                          <option value="full">Full</option>
                          <option value="own">Own only</option>
                          <option value="view">View only</option>
                          <option value="none">No access</option>
                        </select>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="emp-form-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })}/>
              <span className="text-slate-700">Active (can log in)</span>
            </label>

            <div className="flex gap-2 pt-2 border-t border-slate-100">
              <button type="submit" data-testid="emp-form-save" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">
                {editing._new ? "Create account" : "Save changes"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}
